import React, { useState } from 'react';
import { ArrowRight, Send, CheckCircle, Mail, Calendar, Users, Loader2 } from 'lucide-react';
import { bookingData } from '../data/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    eventType: '',
    eventDate: '',
    audienceSize: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/booking/inquiry`, {
        name: formData.name,
        email: formData.email,
        organization: formData.organization,
        event_type: formData.eventType,
        audience_size: formData.audienceSize,
        event_date: formData.eventDate || null,
        message: formData.message
      });
      
      setIsSubmitted(true);
      toast.success('Inquiry submitted successfully!');
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <main className="min-h-screen bg-[#0d0d0d] pt-32 pb-20">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-[#c9a86c]/10 border border-[#c9a86c] rounded-full mx-auto mb-8">
              <CheckCircle size={40} className="text-[#c9a86c]" />
            </div>
            <h1 className="heading-2 text-[#f5f5f0] mb-6">Thank You</h1>
            <p className="body-large text-[#a8a8a0] mb-8">
              Your booking inquiry has been received. Tyrre's team will review your request and respond within 48 hours.
            </p>
            <p className="body-medium text-[#6b6b65]">
              We appreciate your interest in bringing Tyrre to your event.
            </p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-[#0d0d0d]">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="caption text-[#c9a86c] mb-4 block opacity-0 animate-fade-in-up" style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>Book Tyrre</span>
            <h1 className="heading-1 text-[#f5f5f0] mb-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}>
              {bookingData.headline}
            </h1>
            <div className="gold-accent mb-8 opacity-0 animate-fade-in-up" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}></div>
            <p className="body-large text-[#a8a8a0] opacity-0 animate-fade-in-up" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
              {bookingData.intro}
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="pb-20 bg-[#0d0d0d]">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Form */}
            <div className="lg:col-span-7">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-[#f5f5f0]">Your Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="bg-[#1a1a18] border-[rgba(168,168,160,0.2)] text-[#f5f5f0] placeholder:text-[#6b6b65] focus:border-[#c9a86c] h-12"
                      placeholder="John Smith"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#f5f5f0]">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="bg-[#1a1a18] border-[rgba(168,168,160,0.2)] text-[#f5f5f0] placeholder:text-[#6b6b65] focus:border-[#c9a86c] h-12"
                      placeholder="john@company.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="organization" className="text-[#f5f5f0]">Organization *</Label>
                  <Input
                    id="organization"
                    name="organization"
                    value={formData.organization}
                    onChange={handleInputChange}
                    required
                    className="bg-[#1a1a18] border-[rgba(168,168,160,0.2)] text-[#f5f5f0] placeholder:text-[#6b6b65] focus:border-[#c9a86c] h-12"
                    placeholder="Your company or organization"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-[#f5f5f0]">Event Type *</Label>
                    <Select
                      value={formData.eventType}
                      onValueChange={(value) => handleSelectChange('eventType', value)}
                      required
                    >
                      <SelectTrigger className="bg-[#1a1a18] border-[rgba(168,168,160,0.2)] text-[#f5f5f0] h-12">
                        <SelectValue placeholder="Select event type" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a18] border-[rgba(168,168,160,0.2)]">
                        {['Corporate Conference', 'Leadership Summit', 'Faith-Based Event', 'University/College', 'Retreat/Workshop', 'Other'].map((option) => (
                          <SelectItem key={option} value={option} className="text-[#f5f5f0] focus:bg-[#252522] focus:text-[#f5f5f0]">
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#f5f5f0]">Expected Audience Size *</Label>
                    <Select
                      value={formData.audienceSize}
                      onValueChange={(value) => handleSelectChange('audienceSize', value)}
                      required
                    >
                      <SelectTrigger className="bg-[#1a1a18] border-[rgba(168,168,160,0.2)] text-[#f5f5f0] h-12">
                        <SelectValue placeholder="Select audience size" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a18] border-[rgba(168,168,160,0.2)]">
                        {['Under 100', '100-500', '500-1000', '1000+'].map((option) => (
                          <SelectItem key={option} value={option} className="text-[#f5f5f0] focus:bg-[#252522] focus:text-[#f5f5f0]">
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="eventDate" className="text-[#f5f5f0]">Tentative Event Date</Label>
                  <Input
                    id="eventDate"
                    name="eventDate"
                    type="date"
                    value={formData.eventDate}
                    onChange={handleInputChange}
                    className="bg-[#1a1a18] border-[rgba(168,168,160,0.2)] text-[#f5f5f0] h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-[#f5f5f0]">Tell us about your event *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="bg-[#1a1a18] border-[rgba(168,168,160,0.2)] text-[#f5f5f0] placeholder:text-[#6b6b65] focus:border-[#c9a86c] resize-none"
                    placeholder="Share details about your event, what you're hoping to achieve, and any specific topics you'd like Tyrre to address..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full md:w-auto h-14 px-10 text-base"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Inquiry
                      <Send size={18} className="ml-2" />
                    </>
                  )}
                </Button>

                <p className="text-sm text-[#6b6b65]">
                  {bookingData.note}
                </p>
              </form>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-5">
              <div className="bg-[#1a1a18] border border-[rgba(168,168,160,0.1)] p-8 sticky top-32">
                <h3 className="heading-3 text-[#f5f5f0] mb-6">What to Expect</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center border border-[#c9a86c]/30 text-[#c9a86c] flex-shrink-0">
                      <Mail size={18} />
                    </div>
                    <div>
                      <h4 className="text-[#f5f5f0] font-medium mb-1">Response Within 48 Hours</h4>
                      <p className="text-sm text-[#a8a8a0]">Our team reviews every inquiry personally.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center border border-[#c9a86c]/30 text-[#c9a86c] flex-shrink-0">
                      <Calendar size={18} />
                    </div>
                    <div>
                      <h4 className="text-[#f5f5f0] font-medium mb-1">Discovery Call</h4>
                      <p className="text-sm text-[#a8a8a0]">We'll schedule a call to understand your needs.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center border border-[#c9a86c]/30 text-[#c9a86c] flex-shrink-0">
                      <Users size={18} />
                    </div>
                    <div>
                      <h4 className="text-[#f5f5f0] font-medium mb-1">Custom Proposal</h4>
                      <p className="text-sm text-[#a8a8a0]">Receive a tailored proposal for your event.</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-[rgba(168,168,160,0.1)]">
                  <p className="text-sm text-[#6b6b65] mb-2">Direct Contact</p>
                  <a href="mailto:tyrre@tyrreburks.com" className="text-[#c9a86c] hover:text-[#d4af37] transition-colors">
                    tyrre@tyrreburks.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default BookPage;